import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy, Zap, Star, MessageSquareHeart, Target, Send, Sparkles,
  Award, Flame, Users, ChevronDown, Loader2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge icon map — keep pure visual concerns out of the backend.
const BADGE_ICON = {
  discovery_champion:  { icon: Star,   color: '#8B5CF6' },
  revival_master:      { icon: Flame,  color: '#DC2626' },
  objection_handler:   { icon: Target, color: '#0EA5E9' },
  consistency_streak:  { icon: Zap,    color: '#F59E0B' },
  reflection_habit:    { icon: MessageSquareHeart, color: '#14B8A6' },
  kudos_giver:         { icon: Users,  color: '#EC4899' },
  team_player:         { icon: Award,  color: '#10B981' },
};

const _fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }); }
  catch { return iso.slice(0, 10); }
};

// ── Reflection composer ──────────────────────────────────────────────────────
const ReflectionComposer = ({ prompt, params, onSaved }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      await axios.post(`${API}/gamification/reflection?${params}`, {
        text,
        call_id: prompt?.call_id || null,
        prompt: prompt?.text || null,
      });
      toast.success('Reflection saved · +5 XP');
      setText('');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save reflection');
    } finally { setBusy(false); }
  };

  if (!prompt) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="reflection-composer">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
          <MessageSquareHeart className="w-5 h-5 text-teal-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 mb-1">Today&apos;s reflection prompt</p>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">{prompt.text}</p>
          <textarea
            className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none"
            rows={3}
            placeholder="One sentence is enough. Be honest with yourself."
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            data-testid="reflection-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-slate-400">{text.length}/2000 · +5 XP on save</span>
            <button
              type="button"
              onClick={submit}
              disabled={busy || text.trim().length < 3}
              className="text-xs font-semibold bg-teal-600 text-white px-3 py-1.5 rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="reflection-submit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Save reflection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Kudos composer ───────────────────────────────────────────────────────────
const KudosComposer = ({ params, onSent }) => {
  const [peers, setPeers] = useState([]);
  const [to, setTo] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/gamification/peers?${params}`)
      .then(r => setPeers(r.data?.peers || []))
      .catch(() => setPeers([]));
   
  }, [open, params]);

  const submit = async () => {
    if (!to || msg.trim().length < 2) return;
    setBusy(true);
    try {
      await axios.post(`${API}/gamification/kudos?${params}`, { to_agent_id: to, message: msg });
      toast.success('Kudos sent · +1 XP');
      setMsg(''); setTo('');
      onSent?.();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send kudos');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="kudos-composer">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 rounded-2xl transition-colors"
        onClick={() => setOpen(o => !o)}
        data-testid="kudos-composer-toggle"
      >
        <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-pink-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-800">Send kudos to a teammate</p>
          <p className="text-[11px] text-slate-500">Peer feedback that shows up on their dashboard</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-2.5">
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-2 bg-white"
            data-testid="kudos-peer-select"
          >
            <option value="">— pick a teammate —</option>
            {peers.map(p => (
              <option key={p.agent_id} value={p.agent_id}>{p.agent_name}</option>
            ))}
          </select>
          <textarea
            className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-pink-400 resize-none"
            rows={2}
            placeholder='e.g., "Great objection handling on the price talk today."'
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            maxLength={280}
            data-testid="kudos-message"
          />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-400">{msg.length}/280</span>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !to || msg.trim().length < 2}
              className="text-xs font-semibold bg-pink-600 text-white px-3 py-1.5 rounded-lg hover:bg-pink-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="kudos-submit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send kudos
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Kudos inbox ──────────────────────────────────────────────────────────────
const KudosInbox = ({ params, refreshKey }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gamification/kudos/inbox?${params}&limit=6`)
      .then(r => setItems(r.data?.kudos || []))
      .catch(() => setItems([]));
   
  }, [params, refreshKey]);

  if (!items.length) return null;

  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid="kudos-inbox">
      <div className="flex items-center gap-2 mb-2">
        <Award className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-bold text-emerald-800">Kudos from your team</h3>
      </div>
      <ul className="space-y-2">
        {items.map(k => (
          <li key={k.id} className="text-sm bg-white rounded-lg border border-emerald-100 px-3 py-2" data-testid={`kudos-item-${k.id}`}>
            <p className="text-slate-700 leading-snug">&ldquo;{k.message}&rdquo;</p>
            <p className="text-[11px] text-slate-500 mt-1">
              from <span className="font-semibold text-slate-700">{k.from_agent_name}</span>
              <span className="mx-1.5 text-slate-300">·</span>
              {_fmtDate(k.created_at)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── Main card ────────────────────────────────────────────────────────────────
/**
 * <MyGamificationCard />
 *
 * Personal (agent-facing) gamification block. Renders inside MyCoachingTab so
 * the "how am I doing" answer sits next to the "what am I working on" answer.
 * All calls are agent-scoped; supervisors pass the picked `agentId`.
 */
const MyGamificationCard = ({ domainId = null, agentId = null }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const params = React.useMemo(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    if (agentId) p.set('agent_id', agentId);
    p.set('days', '30');
    return p.toString();
  }, [domainId, agentId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gamification/me?${params}`);
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const bump = () => { setRefreshKey(k => k + 1); load(); };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!data || data.enabled === false) return null;

  const isSelf = user?.extension && String(user.extension) === String(data.agent_id);

  return (
    <div className="space-y-4" data-testid="my-gamification-card">
      {/* ── XP + tier header ────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5" data-force-dark="true" data-testid="xp-tier-header">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5" /> {isSelf ? 'Your Rank' : `${data.agent_name}'s Rank`}
            </p>
            <p className="text-3xl font-bold mt-1 tabular-nums">{data.xp_total}<span className="text-lg text-slate-400 ml-1">XP</span></p>
            <p className="text-xs text-slate-300 mt-0.5">
              +{data.xp_this_week} this week · Current streak: <span className="text-white font-semibold">{data.streak.current}</span>
              {data.streak.best > data.streak.current && (
                <span className="text-slate-400"> · best {data.streak.best}</span>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">{data.level.tier}</p>
            {data.level.next_tier && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                {data.level.next_tier_min_xp - data.xp_total} XP to <span className="text-slate-200 font-semibold">{data.level.next_tier}</span>
              </p>
            )}
            <div className="mt-2 h-1.5 w-40 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-amber-300"
                style={{ width: `${data.level.progress_pct}%` }}
                data-testid="xp-progress-bar"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Badge strip ─────────────────────────────────────────────────── */}
      <div>
        <h3 className="text-slate-700 text-xs font-semibold uppercase tracking-wider mb-2">Personal Badges</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {data.badges.map(b => {
            const cfg = BADGE_ICON[b.id] || BADGE_ICON.consistency_streak;
            const Icon = cfg.icon;
            const pct = Math.round((b.progress.have / b.progress.need) * 100);
            return (
              <div
                key={b.id}
                className={`rounded-xl p-2.5 flex flex-col items-center gap-1.5 text-center border transition-all
                  ${b.unlocked
                    ? 'bg-white border-slate-200 shadow-sm'
                    : 'bg-slate-50 border-slate-100 opacity-70'}`}
                title={b.description}
                data-testid={`personal-badge-${b.id}`}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: b.unlocked ? `${cfg.color}20` : '#F1F5F9' }}
                >
                  <Icon className="w-4 h-4" style={{ color: b.unlocked ? cfg.color : '#94A3B8' }} />
                </div>
                <p className={`text-[11px] font-semibold leading-tight ${b.unlocked ? 'text-slate-800' : 'text-slate-500'}`}>{b.label}</p>
                <p className="text-[10px] text-slate-400 tabular-nums">
                  {b.progress.raw_have}/{b.progress.need}
                </p>
                {!b.unlocked && (
                  <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-400" style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Reflection + Kudos (agent-only; supervisors just see badges) ─── */}
      {isSelf && (
        <>
          <ReflectionComposer prompt={data.prompt} params={params} onSaved={bump} />
          <KudosComposer params={params} onSent={bump} />
        </>
      )}

      <KudosInbox params={params} refreshKey={refreshKey} />
    </div>
  );
};

export default MyGamificationCard;
